/* eslint-disable @typescript-eslint/indent */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Formik, FormikHelpers, FormikValues } from 'formik';

import { IFormAlertType, FormStep } from 'types/forms/formTypes';
import { useDraftService } from 'services';
import {
  IBaseDraft,
  ILoadDraftRequest,
  ISaveDraftRequest
} from 'services/draftService';
import { Loader } from 'components/common';

export interface IFormContextType {
  steps: FormStep[];
  currentStepIndex: number;
  currentStepProps?: any;
  formAlert: IFormAlertType[] | null;
  openStepById: (stepId: string, stepProps?: any) => void;
  handleBack: () => void;
  setFormAlert: (formAlert: IFormAlertType | null) => void;
  addFormAlert: (formAlert: IFormAlertType | null) => void;
  saveDraft: (request: ISaveDraftRequest) => Promise<void>;
  setOnChangeValidation: (isEnable: boolean) => void;
  setOnBlurValidation: (isEnable: boolean) => void;
  cleanup: (clickListener: any) => void;
  clickListener: (e: any) => void;
  modal: boolean;
  url: string;
  setModal: (val: boolean) => void;
}

export const FormContext = React.createContext<IFormContextType>({} as IFormContextType);

interface IFormContextProviderProps<T> {
  children: JSX.Element | JSX.Element[];
  initialValues: T & FormikValues;
  draftRecordId?: string;
  draftSuccessReturnUrl?: string;
  draftFailReturnUrl?: string;
  steps: FormStep[];

  // Function called in Application when form submitted
  handleSubmit: (values: T, actions: FormikHelpers<T & FormikValues>) => void | Promise<any>;
}

// FormContext required to share current step info between form components
export const FormContextProvider: React.FC<IFormContextProviderProps<any>> = <T extends any>({
  children,
  steps,
  handleSubmit,
  initialValues,
  draftRecordId,
  draftSuccessReturnUrl,
  draftFailReturnUrl
}: IFormContextProviderProps<T>) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [formAlert, setFormAlert] = useState<IFormAlertType[] | null>(null);
  const [currentStepProps, setCurrentStepProps] = useState(null);
  const [draftLoading, setDraftLoading] = useState<boolean>(true);
  const [existingValues, setExistingValues] = useState<T & FormikValues>(() => initialValues);
  const [onChangeValidation, setOnChangeValidation] = useState(false);
  const [onBlurValidation, setOnBlurValidation] = useState(false);

  const draftService = useDraftService();

  useEffect(() => {
    if (draftRecordId) {
      loadDraft(draftRecordId);
    }
    else {
      setDraftLoading(false);
    }
  }, []);

  /**
   * Set Form Alert
   *
   * @description Set form alert
   * @param {IFormAlertType | null} alert Alert properties
   * @returns {void} Updates state with no return
   */
  const settingFormAlert = (alert: IFormAlertType | null): void => {
    setFormAlert(alert ? [alert] : null);
  };

  const addingFormAlert = (alert: IFormAlertType | null): void => {
    if (alert) {
      if (formAlert) {
        setFormAlert([...formAlert, alert]);
      } else {
        setFormAlert([alert]);
      }
    }
    else {
      setFormAlert(null);
    }
  };

  /**
   * Handle Next
   *
   * @description Method for handling user next click
   * @returns {void} Handles next action no return
   */
  const handleNext = async (values: T, actions: FormikHelpers<T & FormikValues>): Promise<void> => {
    // Navigate to next step as required
    if (currentStepIndex !== steps.filter(step => !step.skipNavigation).length - 1) {
      window.scrollTo(0, 0);
      setFormAlert(null);  // Clear existing alert
      setCurrentStepProps(null);
      setCurrentStepIndex(currentStepIndex + 1);

      cleanup(clickListener);
      return;
    }

    // Handle submission request
    const alert = await handleSubmit(values, actions);
    window.scrollTo(0, 0);
    if (alert) {
      setFormAlert([alert]);
      // If error alert is returned remain on the existing step
      if (alert.as === 'error') {
        return;
      }
    }
    setCurrentStepIndex(steps.length - 1);
  };

  /**
   * Handle Back
   *
   * @description Method for handling user previous click
   * @returns {void} Navigates user back a step
   */
  const handleBack = (): void => {
    if (currentStepIndex > 0) {
      window.scrollTo(0, 0);
      setFormAlert(null); // Clear existing alert
      setCurrentStepProps(null);
      setCurrentStepIndex(currentStepIndex - 1);
    }
  };

  /**
   * Open Step By ID
   *
   * @description Open a specific step using its ID
   * @param {string} stepId Selected stepID
   * @param {any | null} stepProps Required props for the step
   * @returns {void} Navigates user to selected step no return
   */
  const openStepById = (stepId: string, stepProps: any = null): void => {
    window.scrollTo(0, 0);
    const matchingIndex = steps.findIndex(step => step.id === stepId);

    if (matchingIndex < 0) {
      return;
    }

    setCurrentStepProps(stepProps);
    setCurrentStepIndex(matchingIndex);
  };

  /**
   * Save Draft And Exit
   *
   * @description Method triggered when user selects save and exit on the form
   * @returns {void} Saves draft and redirects no return
   */
  const saveDraft = async (request: ISaveDraftRequest): Promise<void> => {
    const response = await draftService.saveFormDraft(request);

    if (response.wasSuccessful) {
      if (draftSuccessReturnUrl) {
        window.location.href = draftSuccessReturnUrl;
      }
      return;
    }

    // eslint-disable-next-line no-console
    console.error('Unable to save draft: ', response.errors);

    // TODO: Get requirements for this error
    const draftFormAlert: IFormAlertType = {
      as: 'error',
      title: 'There was an error saving draft form',
      message: 'Please check form and try again.'
    };
    setFormAlert([draftFormAlert]);
  };

  /**
   * Load Draft
   *
   * @description Loads the draft and updates the form with the draft data
   * @param {string} recordId Draft record ID used to find the draft
   * @returns {void} Loads draft and triggers post load draft action
   */
  const loadDraft = async (recordId: string): Promise<void> => {
    const request: ILoadDraftRequest = {
      recordId: recordId
    };

    try {
      const response = await draftService.loadFormDraft(request);
      if (response.wasSuccessful) {
        // Deserialise draft response
        const deserialisedResponse = JSON.parse(response.form) as IBaseDraft;

        // Update initial values with details loaded from draft
        const values = JSON.parse(deserialisedResponse.formData) as T & FormikValues;

        setExistingValues(values);
        setDraftLoading(false);
        return;
      }
      // TODO: Get requirements for this error
      const draftFormAlert: IFormAlertType = {
        as: 'error',
        title: 'There was an error loading draft form',
        message: 'Please either complete the form again or refresh and try again.'
      };
      setFormAlert([draftFormAlert]);
      return;
    }
    catch (e) {
      if (draftFailReturnUrl) {
        window.location.href = draftFailReturnUrl;
      }
      setDraftLoading(false);
      return;
    }
  };

  const handleOnChangeValidation = (isEnable: boolean): void => {
    setOnChangeValidation(isEnable);
  };

  const handleOnBlurValidation = (isEnable: boolean): void => {
    setOnBlurValidation(isEnable);
  };

  //allow listener to be removed
  const cleanup = useCallback((clickListener) => {
    document.querySelectorAll('a').forEach(item => {
      item.removeEventListener('click', clickListener);
    });
    window.removeEventListener('beforeunload', cleanup);
  }, []);

  const [modal, setModal] = useState(false);
  const [url, setUrl] = useState('');

  const clickListener = useCallback((e) => {
    //if dirty show dialog box
    //if showing then hide and bubble
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore: Object is possibly 'null'.
    if (formikRef.current && formikRef.current.dirty && !modal) {
      e.preventDefault();
      setUrl(e.target.getAttribute('href'));
      setModal(true);
    }
    //if user cancels exit then hide dialog box and exit
    //if user oks exit then hide dialog box and bubble
  }, []);

  const formikRef = useRef(null);

  if (draftLoading) {
    return <Loader message='Working. Please wait.....' />;
  }

  return (
    <Formik
      initialValues={existingValues}
      onSubmit={handleNext}
      validationSchema={steps[currentStepIndex].validationSchema}
      validateOnChange={onChangeValidation}
      validateOnBlur={onBlurValidation}
      enableReinitialize
      innerRef={formikRef}
    >
      <FormContext.Provider value={{
        steps,
        currentStepIndex,
        currentStepProps,
        formAlert,
        openStepById,
        handleBack,
        setFormAlert: settingFormAlert,
        addFormAlert: addingFormAlert,
        saveDraft,
        setOnChangeValidation: handleOnChangeValidation,
        setOnBlurValidation: handleOnBlurValidation,
        cleanup: cleanup,
        clickListener: clickListener,
        modal,
        url,
        setModal: setModal
      }}>
        {children}
      </FormContext.Provider>
    </Formik>
  );
};
